import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  History, Lock, Pencil, Plus, RefreshCw, Save, Trash2, Truck, X,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter 123 — Historial / Notas del cliente.
 *
 * Muestra en orden cronológico inverso todas las entradas del cliente:
 *   - Notas manuales (`kind === "manual"`): editables y borrables por el usuario.
 *   - Registros automáticos (`kind === "auto"`): generados por acciones del
 *     sistema (p.ej. solicitud de recogida/envío). Marcados como "Automática"
 *     con candado; no se pueden editar el usuario/fecha/hora ni el body.
 *
 * Props:
 *   - customerId (string): id del cliente.
 *   - refreshKey (number): incrementar para forzar recarga (p.ej. tras enviar
 *     una solicitud de recogida/envío desde `ShipmentDialog`).
 */
export default function CustomerNotesTimeline({ customerId, refreshKey = 0 }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBody, setNewBody] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/customers/${customerId}/notes`);
      setNotes(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!customerId || customerId === "new") return;
    load();
  }, [customerId, refreshKey]);

  const addNote = async () => {
    const body = newBody.trim();
    if (!body) {
      toast.error("Escribe el contenido de la nota antes de guardar.");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post(`/customers/${customerId}/notes`, { body });
      setNotes((prev) => [r.data, ...prev]);
      setNewBody("");
      toast.success("Nota añadida al historial.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (n) => {
    setEditingId(n.id);
    setEditBody(n.body || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody("");
  };

  const saveEdit = async (n) => {
    const body = editBody.trim();
    if (!body) {
      toast.error("El contenido no puede quedar vacío.");
      return;
    }
    try {
      const r = await api.put(`/customers/${customerId}/notes/${n.id}`, { body });
      setNotes((prev) => prev.map((x) => (x.id === n.id ? r.data : x)));
      cancelEdit();
      toast.success("Nota actualizada.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeNote = async (n) => {
    if (!window.confirm("¿Eliminar esta nota manual del historial?")) return;
    try {
      await api.delete(`/customers/${customerId}/notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
      toast.success("Nota eliminada.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section className="mt-6 ds-card" data-testid="customer-notes-timeline">
      <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between flex-wrap gap-2">
        <div className="font-display font-bold flex items-center gap-2">
          <History size={16} /> Historial del cliente
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            {notes.length} entrada{notes.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="customer-notes-refresh"
            className="p-1 text-[#525252] hover:text-[#111110] disabled:opacity-40"
            title="Refrescar historial"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Formulario para añadir nota manual */}
      <div className="px-4 py-3 border-b border-[#E5E5E0] bg-[#F7F7F4]">
        <div className="flex items-start gap-2">
          <textarea
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            rows={2}
            placeholder="Añade una nota manual al historial del cliente (ej.: llamada, incidencia, aviso...)"
            data-testid="customer-notes-new-input"
            className="flex-1 ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
          />
          <button
            type="button"
            onClick={addNote}
            disabled={creating || !newBody.trim()}
            data-testid="customer-notes-new-save"
            className="inline-flex items-center gap-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <Plus size={14} /> {creating ? "Añadiendo..." : "Añadir"}
          </button>
        </div>
      </div>

      {/* Timeline */}
      {notes.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-[#525252]" data-testid="customer-notes-empty">
          Sin notas ni acciones registradas todavía.
        </div>
      ) : (
        <ul className="divide-y divide-[#E5E5E0]">
          {notes.map((n) => (
            <NoteEntry
              key={n.id}
              note={n}
              editing={editingId === n.id}
              editBody={editBody}
              onEditBodyChange={setEditBody}
              onStartEdit={() => startEdit(n)}
              onCancelEdit={cancelEdit}
              onSaveEdit={() => saveEdit(n)}
              onRemove={() => removeNote(n)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function NoteEntry({
  note, editing, editBody, onEditBodyChange,
  onStartEdit, onCancelEdit, onSaveEdit, onRemove,
}) {
  const isAuto = note.kind === "auto";
  const created = note.created_at ? new Date(note.created_at) : null;
  const dateStr = created ? created.toLocaleDateString("es-ES") : "—";
  const timeStr = created
    ? created.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
    : "—";
  const emailOk = note.email_status === "sent";
  const emailFail = note.email_status === "failed";

  return (
    <li
      className={`px-4 py-3 flex gap-3 ${isAuto ? "bg-[#FBFBF7]" : ""}`}
      data-testid={`customer-note-${note.id}`}
      data-note-kind={note.kind}
    >
      {/* Marker */}
      <div className="mt-0.5 shrink-0">
        {isAuto ? (
          <div className="w-8 h-8 border-2 border-[#111110] bg-white flex items-center justify-center" title="Registro automático">
            <Truck size={14} />
          </div>
        ) : (
          <div className="w-8 h-8 border-2 border-[#6E8B3D] bg-white flex items-center justify-center" title="Nota manual">
            <Pencil size={12} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            {dateStr} · {timeStr}
          </span>
          <span className="text-[#525252]">·</span>
          <span className="text-[#111110]">
            Usuario: <b>{note.created_by_name || "—"}</b>
          </span>
          {isAuto ? (
            <span
              className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#111110] font-mono text-[9px] uppercase tracking-widest bg-[#111110] text-white"
              data-testid="customer-note-badge-auto"
            >
              <Lock size={9} /> Automática
            </span>
          ) : (
            <span
              className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#6E8B3D] font-mono text-[9px] uppercase tracking-widest text-[#6E8B3D]"
              data-testid="customer-note-badge-manual"
            >
              <Pencil size={9} /> Manual
            </span>
          )}
        </div>

        {note.title && (
          <div className="mt-1 font-semibold text-sm">{note.title}</div>
        )}

        {editing ? (
          <div className="mt-1 flex items-start gap-2">
            <textarea
              value={editBody}
              onChange={(e) => onEditBodyChange(e.target.value)}
              rows={2}
              data-testid={`customer-note-edit-input-${note.id}`}
              className="flex-1 ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-2 py-1 text-sm rounded-none"
            />
            <button
              type="button"
              onClick={onSaveEdit}
              data-testid={`customer-note-edit-save-${note.id}`}
              className="inline-flex items-center gap-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-2 py-1 text-xs font-semibold"
            >
              <Save size={12} /> Guardar
            </button>
            <button
              type="button"
              onClick={onCancelEdit}
              data-testid={`customer-note-edit-cancel-${note.id}`}
              className="inline-flex items-center gap-1 border border-[#111110] px-2 py-1 text-xs"
            >
              <X size={12} /> Cancelar
            </button>
          </div>
        ) : (
          <div className="mt-1 text-sm whitespace-pre-wrap">
            {note.body || <span className="italic text-[#525252]">(sin contenido)</span>}
          </div>
        )}

        {/* Estado del email para entradas auto */}
        {isAuto && emailOk && (
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#556C2E]">
            <CheckCircle2 size={11} />
            Email enviado correctamente a <span className="font-mono">{note.email_recipient}</span>
          </div>
        )}
        {isAuto && emailFail && (
          <div className="mt-1 inline-flex items-start gap-1 text-[11px] text-red-700">
            <AlertTriangle size={11} className="mt-0.5" />
            <span>
              Error al enviar email de confirmación
              {note.email_error ? ` · ${note.email_error}` : ""}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      {!isAuto && !editing && (
        <div className="shrink-0 flex items-start gap-1">
          <button
            type="button"
            onClick={onStartEdit}
            data-testid={`customer-note-edit-${note.id}`}
            className="p-1 text-[#525252] hover:text-[#111110]"
            title="Editar nota"
          >
            <Pencil size={12} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            data-testid={`customer-note-delete-${note.id}`}
            className="p-1 text-red-700 hover:text-red-900"
            title="Eliminar nota"
          >
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </li>
  );
}
